import type { TFunction } from 'i18next';
import { z } from 'zod';
import { workIntegrationDto } from '@/components/common/settings/work-integrations/work-integration-dialog/jira/lib/types';

const teamSourceSchema = z.enum(['JIRA_TEAM', 'TEMPO_TEAM']);
export type TeamSourceType = z.infer<typeof teamSourceSchema>;
export const TeamSource = teamSourceSchema.enum;

const syncTeamsStepKeysSchema = z.enum(['connect', 'source', 'done']);
export type SyncTeamsStepKeysType = z.infer<typeof syncTeamsStepKeysSchema>;
export const SyncTeamsStepKey = syncTeamsStepKeysSchema.enum;

export const syncTeamsFormSchema = workIntegrationDto.extend({
  teamSource: teamSourceSchema.optional(),
});
export type SyncTeamsFormType = z.infer<typeof syncTeamsFormSchema>;

const importTeamsStepKeysSchema = z.enum(['connect', 'source', 'teams', 'done']);
export type ImportTeamsStepKeysType = z.infer<typeof importTeamsStepKeysSchema>;
export const ImportTeamsStepKey = importTeamsStepKeysSchema.enum;

const teamSchema = z.object({
  sourceId: z.string(),
  name: z.string(),
  imported: z.boolean(),
});

export const importTeamsFormSchema = syncTeamsFormSchema.extend({
  teams: z.array(teamSchema),
});
export type ImportTeamsFormType = z.infer<typeof importTeamsFormSchema>;

export const teamsDialogsSchema = z.enum(['create', 'sync', 'import']);
export const TeamsDialogParam = teamsDialogsSchema.enum;

const teamsTableColumnSchema = z.enum([
  'table-row-selection-column',
  'teamName',
  'membershipCount',
  'createdAt',
  'updatedAt',
  'sourceType',
  'portfolio',
  'actions',
]);

export type TeamsTableColumnType = z.infer<typeof teamsTableColumnSchema>;
export const TeamsTableColumn = teamsTableColumnSchema.enum;

const teamMembershipTableColumnSchema = z.enum([
  'name',
  'role',
  'skills',
  'joinDate',
  'leaveDate',
  'commitment',
  'location',
  'actions',
]);

export type TeamMembershipTableColumnType = z.infer<typeof teamMembershipTableColumnSchema>;
export const TeamMembershipColumn = teamMembershipTableColumnSchema.enum;

const resourceTableColumnSchema = z.enum([
  'table-row-selection-column',
  'name',
  'teams',
  'portfolioLoops',
  'skills',
  'workloadSchemes',
  'holidaySchemes',
  'location',
  'employmentType',
  'actions',
]);

export type ResourceTableColumnType = z.infer<typeof resourceTableColumnSchema>;
export const ResourceTableColumn = resourceTableColumnSchema.enum;

export type ToggleColumn<T> = {
  columnKey: T;
  getTitle: (t: TFunction) => string;
  isHideable: boolean;
};

export type TeamsTableToggleColumn = ToggleColumn<TeamsTableColumnType>;

export type TeamMembershipTableToggleColumn = ToggleColumn<TeamMembershipTableColumnType>;

export type ResourceTableToggleColumn = ToggleColumn<ResourceTableColumnType>;

export type ColumnVisibilityType<T extends PropertyKey> = Record<T, boolean>;

export type TeamsTableColumnVisibilityType = ColumnVisibilityType<TeamsTableColumnType>;

export type TeamMembershipTableColumnVisibilityType =
  ColumnVisibilityType<TeamMembershipTableColumnType>;

export type ResourceTableColumnVisibilityType = ColumnVisibilityType<ResourceTableColumnType>;

const openPositionTableColumnSchema = z.enum([
  'table-row-selection-column',
  'name',
  'role',
  'skills',
  'location',
  'filledBy',
  'actions',
]);

export type OpenPositionTableColumnType = z.infer<typeof openPositionTableColumnSchema>;
export const OpenPositionTableColumn = openPositionTableColumnSchema.enum;

export type OpenPositionTableToggleColumn = ToggleColumn<OpenPositionTableColumnType>;

export type OpenPositionTableColumnVisibilityType =
  ColumnVisibilityType<OpenPositionTableColumnType>;
